import fileSize from 'filesize'
import JSZip from 'jszip'
import { FileForUpload } from '../ui/components/ResourceUploader/ResourceUploader'

const processProtocol = (url) => {
  if (process.env.REACT_APP_ENV === 'development') {
    return url.replace(/^https:\/\//i, 'http://')
  } else {
    return url
  }
}

export const composeFileName = (file: FileForUpload) => {
  if (file.name) {
    const extSeperator = '.'
    const indexOfNameExt = file.name.lastIndexOf(extSeperator)
    const indexOfPathExt = file.path.lastIndexOf(extSeperator)
    const nameExtension =
      indexOfNameExt > -1 ? file.name.substring(indexOfNameExt) : null
    const pathExtension =
      indexOfPathExt > -1 ? file.path.substring(indexOfPathExt) : null

    if ((!nameExtension && !pathExtension) || nameExtension === pathExtension) {
      return file.name
    }

    const composed =
      (indexOfNameExt > -1
        ? file.name.substring(0, indexOfNameExt)
        : file.name) + pathExtension

    return composed
  }

  return file.path
}

export const uploadFileToStorage = (
  file: FileForUpload,
  { url, onProgress, onError, onFinish, createAbortHandler, contentType }
) => {
  return new Promise((resolve, reject) => {
    if (!file?.object) {
      onError(file)
    }

    const request = new XMLHttpRequest()

    request.open('PUT', url)

    createAbortHandler({
      fileId: file.id,
      abort: () => {
        reject('File aborted')
        request.abort()
      }
    })

    if (contentType) {
      request.setRequestHeader('Content-Type', contentType)
    }

    request.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress({
          fileId: file.id,
          loaded: e.loaded,
          total: e.total
        })
      }
    }

    request.onload = function () {
      if (request.status >= 200 && request.status < 300) {
        onFinish(file)
        resolve(url)
      } else {
        onError(file)
        onFinish(file)
        reject(request.response || 'Upload failed')
      }
    }

    request.onerror = function () {
      onError(file)
      onFinish(file)
      reject(request.response || 'Network error')
    }

    request.send(file.object)
  })
}

const saveBlobAs = (blob, filename) => {
  const elem = window.document.createElement('a')
  elem.href = window.URL.createObjectURL(blob)
  elem.download = filename
  elem.style = 'display:none;opacity:0;color:transparent;'
  ;(document.body || document.documentElement).appendChild(elem)
  if (typeof elem.click === 'function') {
    elem.click()
  } else {
    elem.target = '_blank'
    elem.dispatchEvent(
      new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      })
    )
  }
  URL.revokeObjectURL(elem.href)
}

export const saveBase64Pdf = (base64: string, filename: string) => {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  saveBlobAs(new Blob([bytes], { type: 'application/pdf' }), filename)
}

export const saveUrlAs = (name: string, url: string) => {
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export const downloadFiles = async (files: { name: string; url: string }[]) => {
  const zip = new JSZip()
  const options: any = {
    cache: 'no-store'
  }
  const batchGet = files.map((a) => {
    const url = a.url
    return fetch(processProtocol(url), options)
  })

  const response = await Promise.all(batchGet)
  const responseToBlob = await Promise.all(
    response.map((body: Response) => body.blob())
  )

  responseToBlob.forEach((blob, index) => {
    const file = files[index]
    const fileName = file.name

    zip.file(fileName, blob)
  })

  return zip.generateAsync({ type: 'blob' }).then((content) => {
    saveBlobAs(content, 'Havelock files.zip')
  })
}

export const formatFileSize = (size: number) => {
  if (size < 1024) {
    return '<1 kB'
  }

  return fileSize(size, { base: 2, round: 1 }).replace('MiB', 'MB')
}

const getPresignedUrlExpiresAt = (
  urlStr: string | null | undefined
): number | null => {
  if (!urlStr) return null

  try {
    const { searchParams } = new URL(urlStr)
    const dateStr = searchParams.get('X-Goog-Date')
    const expiresStr = searchParams.get('X-Goog-Expires')

    if (!dateStr || !expiresStr) return null

    const dateMs = Date.UTC(
      +dateStr.slice(0, 4),
      +dateStr.slice(4, 6) - 1,
      +dateStr.slice(6, 8),
      +dateStr.slice(9, 11),
      +dateStr.slice(11, 13),
      +dateStr.slice(13, 15)
    )

    return dateMs + +expiresStr * 1000
  } catch {
    return null
  }
}

export const isPresignedUrlExpiring = (
  url: string | null | undefined
): boolean => {
  const thresholdMs = 1000 * 60 * 10
  const expiresAt = getPresignedUrlExpiresAt(url)

  return expiresAt === null || Date.now() + thresholdMs >= expiresAt
}
